import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import { toast } from '@/components/feedback/toast'
import { normalizeApiError } from '@/lib/apiEnvelope'
import { saveBlob } from '@/lib/download'
import { queryClient } from '@/lib/queryClient'

import { employeeApi } from '../api/employeeApi'
import type {
  EmployeeCreatePayload,
  EmployeeExportFilters,
  EmployeeListFilters,
  EmployeeUpdatePayload,
  ExcluirEmployeePayload,
  TerminateEmployeePayload,
} from '../types/employee'

export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (filters: EmployeeListFilters) => [...employeeKeys.lists(), filters] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (id: string) => [...employeeKeys.details(), id] as const,
}

/** Lista paginada. `keepPreviousData` evita a tabela piscar ao paginar/filtrar. */
export function useEmployees(filters: EmployeeListFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: employeeKeys.list(filters),
    queryFn: () => employeeApi.list(filters),
    placeholderData: keepPreviousData,
    enabled: options?.enabled,
  })
}

export function useEmployee(id: string | undefined) {
  return useQuery({
    queryKey: employeeKeys.detail(id ?? ''),
    queryFn: () => employeeApi.getById(id as string),
    enabled: !!id,
  })
}

// Nos hooks de create/update o erro é exibido pelo próprio formulário
// (FormAlert + erros de campo), então não disparamos toast aqui.
export function useCreateEmployee() {
  return useMutation({
    mutationFn: (payload: EmployeeCreatePayload) => employeeApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
    },
  })
}

export function useUpdateEmployee(id: string) {
  return useMutation({
    mutationFn: (payload: EmployeeUpdatePayload) => employeeApi.update(id, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(employeeKeys.detail(id), updated)
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
    },
  })
}

export function useExportEmployees() {
  return useMutation({
    mutationFn: (filters: EmployeeExportFilters) => employeeApi.exportXlsx(filters),
    onSuccess: ({ blob, fileName }) => saveBlob(blob, fileName),
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

export function useDownloadFichaRegistro(id: string) {
  return useMutation({
    mutationFn: () => employeeApi.downloadFichaRegistro(id),
    onSuccess: ({ blob, fileName }) => saveBlob(blob, fileName),
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

export function useTerminateEmployee(id: string) {
  return useMutation({
    mutationFn: (payload: TerminateEmployeePayload) => employeeApi.terminate(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.all })
      toast.success('Desligamento registrado.')
    },
    onError: (error) => toast.error(normalizeApiError(error).detail),
  })
}

/**
 * Sem toast de erro: o diálogo mostra a mensagem da API (o 409 diz o que resolver antes).
 * O detalhe (e as sub-abas) só é marcado como obsoleto, sem refetch: a ficha ainda fica
 * montada até a navegação concluir, e tanto refazer quanto remover a query dispararia um
 * GET que dá 404. Se o id for aberto de novo, a query obsoleta é refeita e mostra o 404.
 */
export function useExcluirEmployee(id: string) {
  return useMutation({
    mutationFn: (payload: ExcluirEmployeePayload) => employeeApi.excluir(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: employeeKeys.detail(id),
        refetchType: 'none',
      })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
      toast.success('Colaborador excluído.')
    },
  })
}
